// noinspection SpellCheckingInspection

import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { RowChoiceExcludedLegalEntity } from "_shared/_views/tables/row-choice-excluded-legal-entity";
import { RowChoiceRequiresCredentials } from "_shared/_views/tables/row-choice-requires-credentials";
import { DEFAULT_ROW_CHOICE_LABEL, RowChoiceLabel } from "_shared/_views/tables/row-choice-label";
import Guid from "_shared/guids/guid";
import { BookmarkedIndividuals } from "marketplace/bookmarks/_views/bookmarked-individuals";
import Individual from "marketplace/individuals/individual";
import React from "react";
import { NavigateFunction } from "react-router";
import UserCoworkers from "users/information/coworker-information/_views/user-coworkers";
import UserCoworker from "users/information/coworker-information/user-coworker";
import NetworkedIndividuals from "users/networking/connections/_views/components/networked-individuals";
import Marketplace from "../marketplace/vendor-marketplace";
import UserInformation from "users/information/user-information";
import IndividualProfile from "marketplace/individuals/profiles/individual-profile";
import Company from "marketplace/companies/company";
import CompanyProfile from "marketplace/companies/profiles/company-profile";
import { useSelector } from "react-redux";
import { getSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import { useSession } from "users/__session/session-context";

const PageContainer = styled(Box)(() => ({
  display: "grid",
  gridTemplateRows: "auto 1fr",
  minWidth: 0,
  width: "100%",
}));
const TabsContainer = styled("div")(() => ({
  gridRowStart: 1,
  minWidth: 0,
}));
const TabContent = styled("div")(() => ({
  gridRowStart: 2,
  gridRowEnd: "none",
  minWidth: 0,
  overflowX: "hidden",
}));
// The marketplace scrolls its own results under a search bar that stays put, so the tab around it
// must not scroll as well. `overflowX: hidden` alone makes the vertical overflow `auto` too, and the
// tab then scrolled the whole marketplace beside the marketplace's own scroller: two scrollbars, the
// outer one carrying the search bar away. Clipping on both axes leaves the marketplace's the only one.
const MarketplaceTabContent = styled(TabContent)(() => ({
  display: "flex",
  minHeight: 0,
  overflow: "hidden",
}));

type VendorTeamMemberSelectionProps = {
  selectLeader: boolean;
  selectMember: boolean;
  selectedTeamMemberUserIds: Guid[];
  initialActiveTab?: TeamMemberSelectionTab;
  /**
   * What each person's button says. Defaults to "Add To Team", which is what the picker does almost
   * everywhere; a fee schedule rate's picker chooses who bills it, and says "Select".
   */
  choiceLabel?: string;
  /**
   * Whether the person chosen as a member joins a vendor team, which no vendor team allows until
   * their profile lists professional credentials (#5800). Defaults to true, because almost every
   * host seats whoever is chosen on the firm's roster. A client team's picker passes false — its
   * in-house colleagues bill nobody — and so does a fee schedule template's rate, which names who
   * bills it and seats nobody. A leader is never held to it, whatever this says.
   */
  requiresCredentials?: boolean;
  /**
   * A legal entity whose people are not offered on any tab: a proposal's client, when the picker
   * chooses who bills one of its rates (#6091). Where it is the viewer's own firm, the Coworkers tab
   * would offer nobody else, so it is not drawn.
   */
  excludedLegalEntityId?: Guid;
  searchParams?: URLSearchParams;
  navigate?: NavigateFunction;
  /** Told when the marketplace tab's filters open over it, so the panel around it can make room. */
  onFilterViewOpenChange?: (isOpen: boolean) => void;
  onNewSelection: (teamMember: Individual) => void;
  closeDialog: () => void;
};

type TeamMemberSelectionTab = "marketplace" | "coworkers" | "network" | "indvBookmarks";

export default function VendorTeamMemberSelection(props: Readonly<VendorTeamMemberSelectionProps>) {
  const { 
    selectLeader, 
    selectMember, 
    selectedTeamMemberUserIds, 
    initialActiveTab,
    choiceLabel,
    requiresCredentials,
    excludedLegalEntityId,
    navigate, 
    searchParams,
    onFilterViewOpenChange,
    onNewSelection,
    closeDialog
  } = props;

  const userPermissions = useSelector(getSessionPermissions);
  const session = useSession();
  const marketplaceAvailable = Boolean(userPermissions?.includesAccessToMarketplaceSearch);
  const coworkersAvailable = !excludedLegalEntityId?.isEqualTo(session.user?.legalEntityId);
  const fallbackTab = coworkersAvailable ? "coworkers" : "network";
  const requestedTab = initialActiveTab === "coworkers" && !coworkersAvailable ? undefined : initialActiveTab;
  const defaultActiveTab = requestedTab === "marketplace" && !marketplaceAvailable
    ? fallbackTab
    : requestedTab ?? (marketplaceAvailable ? "marketplace" : fallbackTab);

  const [activeTab, setActiveTab] = React.useState(defaultActiveTab);
  const [filterViewIsOpen, setFilterViewIsOpen] = React.useState(false);
  // Stable on purpose: the marketplace reports through it from an effect keyed on it, so a fresh
  // function each render would report again every render.
  const handleFilterViewOpenChange = React.useCallback(
    (isOpen: boolean) => {
      setFilterViewIsOpen(isOpen);
      onFilterViewOpenChange?.(isOpen);
    },
    [onFilterViewOpenChange],
  );
  const choiceRequiresCredentials = selectMember && !selectLeader && requiresCredentials !== false;

  function marketplaceIndividualSelected(individual: Individual) {
    if (!individual.profile) throw new Error("Individual profile is required.");
    onNewSelection(individual);
    closeDialog();
  }

  function bookmarkedIndividualSelected(individual: Individual) {
    onNewSelection(individual);
    closeDialog();
  }

  async function networkedIndividualSelected(connection: UserInformation) {
    if (!connection.individualId || !connection.id || !connection.legalEntityId) {
      throw new Error("Networked individual must have an individualId, legalEntityId, and userId");
    }

    // TODO: Pull from Redux store
    const individual = Individual.create({
      id: connection.individualId,
      userId: connection.id,
      profile: IndividualProfile.create({
        firstName: connection.name.firstName ?? "",
        lastName: connection.name.lastName ?? "",
        avatarId: connection.avatarId,
        email: connection.email,
        legalEntityId: connection.legalEntityId,
        userId: connection.id,

      }),
      isVisible: false,
      legalEntityId: connection.legalEntityId,
      company: connection.companyId ? Company.create({
        id: connection.companyId,
        legalEntityId: connection.legalEntityId,
        profile: CompanyProfile.create({
          name: connection.companyName
        }),
        isVisible: connection.companyProfileVisible
      }) : undefined,
    });
    onNewSelection(individual);
    closeDialog();
  }

  async function userCoworkerSelected(coworker: UserCoworker) {
    if (!coworker.individualId || !coworker.userId) throw new Error("Coworker must have an individualId and userId");

    onNewSelection(coworker.asIndividual());
    closeDialog();
  }

  return (
    <RowChoiceLabel value={choiceLabel ?? DEFAULT_ROW_CHOICE_LABEL}>
      <RowChoiceRequiresCredentials value={choiceRequiresCredentials}>
      <RowChoiceExcludedLegalEntity value={excludedLegalEntityId}>
        <PageContainer>
          {/* Hidden while the marketplace's filters are open over the results: those filters belong to
              the Marketplace tab, and switching source under them left them filtering nothing (#6040). */}
          <TabsContainer hidden={filterViewIsOpen}>
            <Tabs
              indicatorColor="primary"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              value={activeTab}
              onChange={(event, tab) => setActiveTab(tab)}
              aria-label="Attorney selection nav"
              sx={{
                minHeight: 40,
                maxWidth: "100%",
                "& .MuiTab-root": {
                  minHeight: 40,
                  minWidth: "auto",
                  px: 1.25,
                },
              }}
            >
              {marketplaceAvailable && (
                <Tab value="marketplace" label="Marketplace" />
              )}
              {coworkersAvailable && <Tab value="coworkers" label="Coworkers" />}
              <Tab value="network" label="Network" />
              <Tab value="indvBookmarks" label="Bookmarked Individuals" />
            </Tabs>
          </TabsContainer>
          {marketplaceAvailable && activeTab === "marketplace" && (
            <MarketplaceTabContent>
              <Marketplace
                navigate={navigate}
                selectingLeader={selectLeader}
                selectingMember={selectMember}
                selectedTeamMemberUserIds={selectedTeamMemberUserIds}
                selectingTeam={false}
                searchParams={searchParams}
                onContainedFilterOpenChange={handleFilterViewOpenChange}
                onTeamMemberSelected={marketplaceIndividualSelected}
                onTeamSelected={() => {}}
              />
            </MarketplaceTabContent>
          )}
          {coworkersAvailable && activeTab === "coworkers" && (
            <TabContent>
              <UserCoworkers selectedMemberUserIds={selectedTeamMemberUserIds} onSelected={userCoworkerSelected} />
            </TabContent>
          )}
          {activeTab === "network" && (
            <TabContent>
              <NetworkedIndividuals
                selectedMemberUserIds={selectedTeamMemberUserIds}
                onSelected={networkedIndividualSelected}
              />
            </TabContent>
          )}
          {activeTab === "indvBookmarks" && (
            <TabContent>
              <BookmarkedIndividuals
                selectedMemberUserIds={selectedTeamMemberUserIds}
                onSelected={bookmarkedIndividualSelected}
              />
            </TabContent>
          )}
        </PageContainer>
      </RowChoiceExcludedLegalEntity>
      </RowChoiceRequiresCredentials>
    </RowChoiceLabel>
  );
}
